import React, { useState, useCallback, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { requestOpenContent } from '../../utils/siteContent';
import { authService } from '../../services/authService';
import { CountdownButton } from './CountdownButton';
import { WeChatQRSection } from './WeChatQRSection';

/** 输入框通用样式：白底 / rgba(20,20,20,0.25) 描边 / 圆角 12px（新稿 121:28 等节点） */
const fieldBorder: React.CSSProperties = {
  border: '1px solid rgba(20, 20, 20, 0.25)',
  transition: 'border-color 0.15s',
};

/** 已填写文字态：#141414 Semibold + 0.1em 字距（新稿手机号/邀请码已填样式） */
const filledTextStyle = (filled: boolean): React.CSSProperties => ({
  color: '#141414',
  fontWeight: filled ? 600 : 400,
  letterSpacing: filled ? '0.1em' : 'normal',
});

/**
 * 登录弹窗 —— 按 Figma 新稿 4 节点还原并做响应式：
 * 121:28 默认态 / 121:147 已注册态 / 131:2 未注册需邀请码态 / 140:2 已填邀请码态
 *
 * 骨架：720x560 白卡（banner 126 + 左二维码 188x238 + 右表单 320 + 底部协议条 64）
 * 状态机：手机号合法后 300ms 防抖 checkPhone →
 *   已注册   → 绿色提示条顶替邀请码框，获取验证码/登录按钮激活（表单列收窄重排）
 *   未注册   → 邀请码白底必填，填入后激活按钮
 *   未知/查询中 → 邀请码灰底禁用观感，按钮置灰（填了邀请码也放行，由后端兜底校验）
 * 响应式：≥768px 双栏 720 固定卡 / <768px 单列流式（banner 压缩 + 表单全宽 + 紧凑二维码横条）
 */
export const LoginModal: React.FC = () => {
  const { isLoginModalOpen, closeLoginModal, login, sessionExpired } = useAuthStore();
  const [phone, setPhone] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 手机号注册状态：null=未知（手机号未输满/查询中），true=已注册，false=新用户 */
  const [isRegistered, setIsRegistered] = useState<boolean | null>(null);

  const isPhoneValid = /^1[3-9]\d{9}$/.test(phone);
  const isCodeValid = /^\d{6}$/.test(code);

  // 流程就绪 = 手机号合法 且（已注册 或 已填邀请码）；
  // isRegistered 为 null（查询失败）时填了邀请码也放行，登录时后端强制校验兜底
  const canProceed = isPhoneValid && (isRegistered === true || inviteCode.trim().length > 0);

  // 手机号输满 11 位且合法时，300ms 防抖查询是否已注册，
  // 据此差异化显示邀请码栏：已注册 → 绿色提示条；新用户 → 白底必填
  useEffect(() => {
    if (!isPhoneValid) {
      setIsRegistered(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { registered } = await authService.checkPhone(phone);
        if (!cancelled) setIsRegistered(registered);
      } catch {
        // 查询失败保持未知态，不阻塞流程：登录时后端会强制校验邀请码
        if (!cancelled) setIsRegistered(null);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [phone, isPhoneValid]);

  const handleSendCode = useCallback(async () => {
    if (!canProceed) {
      setError(isRegistered === false ? '请先输入邀请码' : '请输入正确的手机号');
      return;
    }
    setError(null);
    try {
      await authService.sendCode(phone);
    } catch (e: any) {
      setError(e?.message || '发送验证码失败');
    }
  }, [phone, canProceed, isRegistered]);

  const handleLogin = useCallback(async () => {
    if (!isPhoneValid) {
      setError('请输入正确的手机号');
      return;
    }
    if (isRegistered === false && !inviteCode.trim()) {
      setError('新用户注册需填写邀请码');
      return;
    }
    if (!isCodeValid) {
      setError('请输入 6 位验证码');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await login(phone, code, inviteCode.trim() || undefined);
      setPhone('');
      setInviteCode('');
      setCode('');
      setIsRegistered(null);
    } catch (e: any) {
      setError(e?.message || '登录失败，请重试');
    } finally {
      setIsLoading(false);
    }
  }, [phone, code, inviteCode, isPhoneValid, isCodeValid, isRegistered, login]);

  const handleClose = () => {
    closeLoginModal();
    setPhone('');
    setInviteCode('');
    setCode('');
    setError(null);
    setIsRegistered(null);
  };

  if (!isLoginModalOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center overflow-y-auto p-4 animate-in fade-in duration-200"
      style={{ backgroundColor: 'rgba(20, 20, 20, 0.65)' }}
      onClick={handleClose}
    >
      {/* 卡片 wrapper：桌面 720x560 固定卡（<768px 宽度撑满、遮罩层滚动），原整页缩放系数注入已随 useViewportScale 退役移除 */}
      <div
        className="my-auto w-full max-w-[720px] md:h-[560px] md:w-[720px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 卡片：桌面 720x560 固定 / 小屏宽度撑满、高度自适应（遮罩层可滚动） */}
        <div
          className="relative flex h-full max-h-full w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 duration-150"
        >
        {/* 顶部 banner：720x126 整图切图（含能力标签），小屏压缩至 96px cover 裁剪 */}
        <div
          className="relative h-[96px] w-full shrink-0 md:h-[126px]"
          style={{ backgroundColor: 'rgba(115, 196, 250, 1)' }}
        >
          <img
            src="/icons/login/banner.png"
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>

        {/* 会话过期提示：浮于 banner 下缘，不参与布局流 */}
        {sessionExpired && (
          <div
            className="absolute left-1/2 z-10 flex -translate-x-1/2 items-center whitespace-nowrap rounded-full"
            style={{
              top: '108px',
              padding: '6px 16px',
              backgroundColor: '#FFF1F0',
              fontSize: '12px',
              lineHeight: '12px',
              color: '#E54545',
            }}
          >
            登录已过期，请重新登录后查看你的数据
          </div>
        )}

        {/* 主体：桌面双栏（左二维码 + 右表单 + 独立登录按钮）/ 小屏单列 */}
        <div className="flex min-h-0 flex-1 flex-col px-6 pb-5 md:block md:px-0 md:pb-0">
          {/* 标题：banner 底下方 48px（桌面），20px Semibold。
              桌面端文字盒锁定到右侧表单列几何范围（ml=64 容器+188 二维码+74 间隔=326，宽 320），
              使「手机号登录」与下方输入框左右中心对齐；小屏仍是整卡居中 */}
          <h2
            className="mt-6 shrink-0 text-center text-[20px] font-semibold leading-[20px] text-[#141414] md:ml-[326px] md:mt-[48px] md:w-[320px]"
            style={{ letterSpacing: '-0.04em' }}
          >
            手机号登录
          </h2>

          <div className="mt-4 flex min-h-0 flex-1 flex-col gap-4 md:mt-[24px] md:flex-row md:gap-0 md:pl-[64px]">
            {/* 左侧微信二维码（仅桌面）：188x238 + 74px 间隔 → 表单列左缘 326px */}
            <div className="hidden shrink-0 md:block" style={{ marginRight: '74px' }}>
              <WeChatQRSection />
            </div>

            {/* 右侧表单列：320px（桌面）/ 全宽（小屏） */}
            <div className="flex w-full min-w-0 flex-col md:w-[320px]">
              {/* 手机号输入：320x48 / 白底 / rgba(20,20,20,0.25) 描边 / 圆角 12px，+86 + 分隔线 */}
              <div className="flex h-12 w-full items-center rounded-xl bg-white focus-within:border-[rgba(20,20,20,0.6)]" style={fieldBorder}>
                <span className="shrink-0 text-[14px] font-semibold leading-[14px] text-[#141414]" style={{ marginLeft: '15px' }}>+86</span>
                <span className="shrink-0 bg-[rgba(20,20,20,0.15)]" style={{ width: '1px', height: '20px', marginLeft: '12px' }} />
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  disabled={isLoading}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  placeholder="请输入手机号"
                  className="w-full bg-transparent text-[14px] leading-[14px] outline-none placeholder:text-[rgba(20,20,20,0.45)]"
                  style={{ marginLeft: '12px', marginRight: '16px', ...filledTextStyle(isPhoneValid) }}
                />
              </div>

              {/* 注册状态差异区：已注册 → 绿色提示条（顶替邀请码框，表单列重排）；
                  其余 → 邀请码框（未知态灰底 / 未注册白底，占位文案区分） */}
              {isRegistered === true ? (
                <div
                  className="flex w-full items-center justify-center rounded-lg"
                  style={{ height: '30px', marginTop: '6px', backgroundColor: 'rgba(0, 96, 62, 0.05)' }}
                >
                  <img src="/icons/login/check-icon.svg" alt="" width={12} height={12} />
                  <span className="whitespace-nowrap" style={{ marginLeft: '6px', fontSize: '10px', lineHeight: '10px', color: '#00603E' }}>
                    手机号已注册，无需再次验证邀请码
                  </span>
                </div>
              ) : (
                <div
                  className="flex h-12 w-full items-center rounded-xl focus-within:border-[rgba(20,20,20,0.6)]"
                  style={{
                    ...fieldBorder,
                    marginTop: '10px',
                    backgroundColor: isRegistered === null ? '#F8F8F8' : '#FFFFFF',
                  }}
                >
                  <input
                    type="text"
                    value={inviteCode}
                    disabled={isLoading}
                    onChange={(e) => setInviteCode(e.target.value.trim().slice(0, 32))}
                    placeholder={
                      isRegistered === false
                        ? '请输入邀请码（可通过左侧二维码申请获得）'
                        : '验证邀请码（已注册手机号将自动跳过）'
                    }
                    className="w-full bg-transparent text-[14px] leading-[14px] outline-none placeholder:text-[rgba(20,20,20,0.45)]"
                    style={{ marginLeft: '15px', marginRight: '16px', ...filledTextStyle(inviteCode.length > 0) }}
                  />
                </div>
              )}

              {/* 验证码行：320x48 白底描边圆角 12，内嵌 94x32 获取验证码按钮 */}
              <div className="relative h-12 w-full rounded-xl bg-white focus-within:border-[rgba(20,20,20,0.6)]" style={{ ...fieldBorder, marginTop: '12px' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  value={code}
                  disabled={isLoading}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="请输入验证码"
                  className="h-full w-full bg-transparent text-[14px] leading-[14px] outline-none placeholder:text-[rgba(20,20,20,0.45)]"
                  style={{ paddingLeft: '15px', paddingRight: '110px', ...filledTextStyle(isCodeValid) }}
                />
                <CountdownButton onSend={handleSendCode} active={canProceed && !isLoading} />
              </div>

              {/* 登录按钮：320x48 圆角 12；未就绪 rgba(20,20,20,0.25)，就绪 #141414（新稿 121:147 / 140:2 激活态） */}
              <button
                onClick={handleLogin}
                disabled={isLoading}
                className="h-12 w-full shrink-0 rounded-xl text-[16px] text-white transition-colors"
                style={{
                  marginTop: '24px',
                  backgroundColor: canProceed ? '#141414' : 'rgba(20, 20, 20, 0.25)',
                  cursor: isLoading ? 'wait' : canProceed ? 'pointer' : 'not-allowed',
                }}
              >
                {isLoading ? '登录中...' : '登录/注册'}
              </button>
            </div>
          </div>

          {/* 小屏紧凑二维码横条（桌面隐藏）：保留「扫码申请邀请码」入口 */}
          <div className="mt-4 md:hidden">
            <WeChatQRSection compact />
          </div>
        </div>

        {/* 底部协议条：720x64 / #F5F6FA / 文字 12px 居中（新稿无勾选框，登录即视为同意）；
            协议链接 → 站点内容页（关闭弹窗后经 CustomEvent 切 content 视图） */}
        <div className="flex w-full shrink-0 items-center justify-center bg-[#F5F6FA] px-4 py-3 md:h-[64px] md:py-0">
          <p className="text-center text-[12px] leading-[16px] text-[#666666]">
            登录即表示阅读并同意
            <span
              className="cursor-pointer"
              style={{ color: '#0092FF' }}
              onClick={() => {
                closeLoginModal();
                requestOpenContent('user-agreement');
              }}
            >《用户协议》</span>
            和
            <span
              className="cursor-pointer"
              style={{ color: '#0092FF' }}
              onClick={() => {
                closeLoginModal();
                requestOpenContent('privacy-policy');
              }}
            >《隐私政策》</span>
            ，未注册手机号将自动注册
          </p>
        </div>

        {/* 错误提示：浮于协议条上方，不破坏布局流 */}
        {error && (
          <div className="pointer-events-none absolute bottom-[68px] left-0 right-0 text-center text-[12px] leading-[12px] text-[#E54545]">
            {error}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};
